/**
 * "Open the composer" as a URL, so the shell can ask for it.
 *
 * The compose window is owned by `MailView` — it holds the draft, the reply
 * seed and the send mutation — but the Compose button lives in the shell's
 * sidebar, which renders *above* the routed view and cannot reach that state.
 * The shell therefore asks through the one channel they do share, the URL,
 * exactly as folder and open message are already expressed there.
 *
 * It is a one-shot *intent*, not view state: `MailView` consumes the parameter
 * as it opens the dialog and rewrites the URL without it. Leaving it set would
 * mean the composer reopened on every back-navigation to a URL that still
 * carried it, and that a closed composer could not stay closed.
 */
export const COMPOSE_PARAM = 'compose';

/** The only intent the beta's send lane understands (doc 11 §10). */
export const COMPOSE_INTENT_NEW = 'new';

/**
 * The recipient a compose intent carries, when it has one.
 *
 * "Email this contact" used to be a `mailto:` link, which hands the person to
 * whatever desktop client the OS has registered -- in a webmail client, the
 * one place they did not ask to go. The seed rides the URL beside
 * {@link COMPOSE_PARAM} so the shell, Contacts and (later) a `mailto:` handler
 * all ask for the composer the same way, and so the one-shot intent stays one
 * shot: `MailView` consumes both parameters together and rewrites the URL.
 *
 * Only an address goes here. A subject or body in a URL is how a link becomes
 * a way to put words in someone's mouth, and nothing in the app needs it.
 */
export const COMPOSE_TO_PARAM = 'to';

/** A plausible address, checked before it is put in a URL or a seed. */
export function isPlausibleAddress(value: string): boolean {
  const trimmed = value.trim();
  if (trimmed.length === 0 || trimmed.length > 320) return false;
  // Deliberately loose -- the server is the authority on deliverability. This
  // only refuses what would break the URL or the composer: whitespace,
  // control characters, and anything that is not one local@domain pair.
  // Controls are checked by code point rather than by a regex class, which
  // keeps the pattern readable and the linter's no-control-regex rule honest.
  for (const character of trimmed) {
    const code = character.codePointAt(0) ?? 0;
    if (code < 0x20 || code === 0x7f) return false;
  }
  return /^[^\s@]+@[^\s@]+$/.test(trimmed);
}
